import { makeAutoObservable, runInAction } from "mobx";
import http from "../services/http";

export interface IFeatureMeta {
  code: string;
  title: string;
  description?: string;
  unit?: string;
  type?: string;
}

export interface IPaymentSubscription {
  id: string;
  status: "active" | "failed" | "canceled" | "completed";
  amount: number;
  currency: string;
  interval: "Month";
  period: number;
  nextPaymentAt: string | null;
  canCancel: boolean;
}

export interface ICompanyFeature {
  id: string;
  feature: IFeatureMeta;
  quantity: number;
  expiresAt: Date | null;
  createdAt: Date;
  subscription?: IPaymentSubscription | null;
}

class CompanyFeatureStore {
  data: ICompanyFeature[] = [];
  isLoading = false;
  cancelingSubscriptionIds: string[] = [];
  cancellationErrors: Record<string, string> = {};

  constructor() {
    makeAutoObservable(this);
  }

  async fetchAll() {
    this.isLoading = true;

    try {
      const res = await http.get("/company-features");

      runInAction(() => {
        this.data = res.data.data.map((f: any) => ({
          ...f,
          createdAt: new Date(f.createdAt),
          expiresAt: f.expiresAt ? new Date(f.expiresAt) : null,
        }));
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async fetchActive() {
    this.isLoading = true;

    try {
      const res = await http.get("/company-features", {
        params: { active: true, includeSubscriptions: true },
      });

      runInAction(() => {
        this.data = res.data.data.map((f: any) => ({
          ...f,
          createdAt: new Date(f.createdAt),
          expiresAt: f.expiresAt ? new Date(f.expiresAt) : null,
        }));
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async cancelSubscription(subscriptionId: string) {
    if (this.cancelingSubscriptionIds.includes(subscriptionId)) return;
    this.cancelingSubscriptionIds.push(subscriptionId);
    delete this.cancellationErrors[subscriptionId];

    try {
      const res = await http.post(`/payment-subscriptions/${subscriptionId}/cancel`);
      runInAction(() => {
        this.data.forEach((feature) => {
          if (feature.subscription?.id === subscriptionId) {
            feature.subscription = res.data.data;
          }
        });
      });
    } catch (error: any) {
      runInAction(() => {
        this.cancellationErrors[subscriptionId] = error?.response?.data?.message
          || error?.message || "Не удалось отключить автопродление. Попробуйте ещё раз.";
      });
    } finally {
      runInAction(() => {
        this.cancelingSubscriptionIds = this.cancelingSubscriptionIds.filter((id) => id !== subscriptionId);
      });
    }
  }

  async applyCoupon(coupon: string) {
    await http.post("/coupon/apply", { coupon });
  }

  isActive(code: string) {
    return this.data.some(f => f.feature?.code === code
      && (!f.expiresAt || f.expiresAt.getTime() > Date.now()));
  }

  getByCode(code: string) {
    return this.data.filter(f => f.feature?.code === code);
  }

  isAlwaysAvailableFeature(code: string) {
    return ["additional_employees", "additional_storage"].includes(code);
  }
}

export default new CompanyFeatureStore();
